import { t } from 'i18next';

type FreshCaptchaSource = {
  isCaptchaRequired: boolean;
  executeCaptcha: () => Promise<string | undefined>;
};

type FreshCaptchaFailure = {
  token?: never;
  errorMessage?: string;
};

type FreshCaptchaSuccess = {
  token: string;
  errorMessage?: never;
};

export type FreshCaptchaResult = FreshCaptchaFailure | FreshCaptchaSuccess;

/**
 * Require a newly issued, non-blank CAPTCHA proof for every verification-code
 * send or resend. Never reuse a previous token or continue without a proof.
 */
export const requestFreshCaptchaProof = async ({
  isCaptchaRequired,
  executeCaptcha,
}: FreshCaptchaSource): Promise<FreshCaptchaResult> => {
  if (!isCaptchaRequired) {
    return { errorMessage: t('error.captcha_verification_failed') };
  }

  try {
    const captchaToken = await executeCaptcha();

    if (typeof captchaToken !== 'string' || captchaToken.trim().length === 0) {
      return { errorMessage: t('error.captcha_verification_failed') };
    }

    return { token: captchaToken };
  } catch {
    // The provider already reports its own failure. Do not continue to a
    // delivery endpoint after a cancelled, failed, or unavailable challenge.
    return {};
  }
};
